/**
 * RichContent
 *
 * 문자열 콘텐츠의 형식(JSON / 마크다운 / 평문)을 판별해 그에 맞게 렌더링한다.
 * 주로 AI 분석 결과처럼 서버가 마크다운을 줄 수도, JSON을 줄 수도, 평문을 줄 수도 있는
 * 텍스트를 보여줄 때 사용한다.
 *
 * - JSON  : 파싱 후 들여쓰기된 코드블록(`<pre>`)으로 표시
 * - 마크다운: react-markdown(+remark-gfm)으로 렌더링 (제목/굵게/목록/표/코드/링크 등)
 * - 평문  : 줄바꿈을 보존하는 pre-wrap `<p>`로 표시
 */

import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { designTokens } from '@/utils/design-tokens'

export type ContentFormat = 'json' | 'markdown' | 'plaintext'

/**
 * 콘텐츠 형식 판별.
 * 1) 객체/배열로 파싱되면 JSON
 * 2) 마크다운 문법 지표가 하나라도 있으면 마크다운
 * 3) 그 외 평문
 */
export function detectContentFormat(raw: string): ContentFormat {
  const text = (raw ?? '').trim()
  if (!text) return 'plaintext'

  // 1) JSON — 객체/배열 형태이고 실제로 파싱되는 경우만
  if (
    (text.startsWith('{') && text.endsWith('}')) ||
    (text.startsWith('[') && text.endsWith(']'))
  ) {
    try {
      const parsed = JSON.parse(text)
      if (parsed !== null && typeof parsed === 'object') return 'json'
    } catch {
      // JSON 아님 → 계속 판별
    }
  }

  // 2) 마크다운 문법 지표
  const markdownPatterns: RegExp[] = [
    /^#{1,6}\s+\S/m, // 제목 (# ~ ######)
    /^\s*[-*+]\s+\S/m, // 불릿 목록
    /^\s*\d+\.\s+\S/m, // 순서 목록
    /^\s*>\s+\S/m, // 인용구
    /\*\*[^*\n]+\*\*/, // **굵게**
    /__[^_\n]+__/, // __굵게__
    /`[^`\n]+`/, // `인라인 코드`
    /```/, // 코드펜스
    /\[[^\]\n]+\]\([^)\n]+\)/, // [링크](url)
    /^\s*\|.+\|\s*$/m, // 표 행
    /^\s*(?:[-*_]\s*){3,}$/m, // 구분선
  ]
  if (markdownPatterns.some((re) => re.test(text))) return 'markdown'

  return 'plaintext'
}

interface RichContentProps {
  content: string
  /** 기본 폰트 크기(px). 기본 14 */
  fontSize?: number
}

const RichContent: React.FC<RichContentProps> = ({ content, fontSize = 14 }) => {
  const format = useMemo(() => detectContentFormat(content), [content])

  const sans = designTokens.typography.fontFamily.sans[0]
  const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

  // ===== JSON =====
  if (format === 'json') {
    let pretty = content
    try {
      pretty = JSON.stringify(JSON.parse(content), null, 2)
    } catch {
      pretty = content
    }
    return (
      <pre
        style={{
          fontFamily: mono,
          fontSize: `${fontSize - 1}px`,
          lineHeight: 1.5,
          color: designTokens.colors.gray[6],
          backgroundColor: designTokens.colors.gray[1],
          padding: '12px',
          borderRadius: '4px',
          margin: 0,
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {pretty}
      </pre>
    )
  }

  // ===== 평문 =====
  if (format === 'plaintext') {
    return (
      <p
        style={{
          fontFamily: sans,
          fontSize: `${fontSize}px`,
          color: designTokens.colors.gray[6],
          whiteSpace: 'pre-wrap',
          margin: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {content}
      </p>
    )
  }

  // ===== 마크다운 =====
  const components: Components = {
    p: ({ children }) => (
      <p style={{ margin: '0 0 8px', lineHeight: 1.6, color: designTokens.colors.gray[6] }}>
        {children}
      </p>
    ),
    h1: ({ children }) => (
      <h1 style={{ fontSize: `${fontSize + 8}px`, fontWeight: 700, margin: '12px 0 8px', color: designTokens.colors.gray[6] }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: `${fontSize + 4}px`, fontWeight: 700, margin: '12px 0 6px', color: designTokens.colors.gray[6] }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: `${fontSize + 2}px`, fontWeight: 600, margin: '10px 0 6px', color: designTokens.colors.gray[6] }}>
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 style={{ fontSize: `${fontSize}px`, fontWeight: 600, margin: '8px 0 4px', color: designTokens.colors.gray[6] }}>
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 style={{ fontSize: `${fontSize}px`, fontWeight: 600, margin: '8px 0 4px', color: designTokens.colors.gray[6] }}>
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 style={{ fontSize: `${fontSize}px`, fontWeight: 600, margin: '8px 0 4px', color: designTokens.colors.gray[5] }}>
        {children}
      </h6>
    ),
    ul: ({ children }) => (
      <ul style={{ paddingLeft: '20px', margin: '0 0 8px', listStyleType: 'disc' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ paddingLeft: '20px', margin: '0 0 8px', listStyleType: 'decimal' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ margin: '2px 0', lineHeight: 1.6, color: designTokens.colors.gray[6] }}>{children}</li>
    ),
    strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
    em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
    a: ({ children, href }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: designTokens.colors.secondary.dark, textDecoration: 'underline' }}
      >
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderLeft: `3px solid ${designTokens.colors.gray[2]}`,
          paddingLeft: '12px',
          margin: '0 0 8px',
          color: designTokens.colors.gray[5],
        }}
      >
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr style={{ border: 0, borderTop: `1px solid ${designTokens.colors.gray[1]}`, margin: '12px 0' }} />
    ),
    code: ({ className, children }) => {
      const isBlock = /language-/.test(className || '')
      if (isBlock) {
        return <code style={{ fontFamily: mono, fontSize: `${fontSize - 1}px` }}>{children}</code>
      }
      return (
        <code
          style={{
            fontFamily: mono,
            fontSize: `${fontSize - 1}px`,
            backgroundColor: designTokens.colors.gray[1],
            padding: '1px 4px',
            borderRadius: '3px',
          }}
        >
          {children}
        </code>
      )
    },
    pre: ({ children }) => (
      <pre
        style={{
          backgroundColor: designTokens.colors.gray[1],
          padding: '12px',
          borderRadius: '4px',
          margin: '0 0 8px',
          overflowX: 'auto',
        }}
      >
        {children}
      </pre>
    ),
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '0 0 8px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: `${fontSize - 1}px` }}>
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th
        style={{
          border: `1px solid ${designTokens.colors.gray[2]}`,
          backgroundColor: designTokens.colors.gray[0],
          padding: '6px 8px',
          textAlign: 'left',
          fontWeight: 600,
          color: designTokens.colors.gray[6],
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          border: `1px solid ${designTokens.colors.gray[2]}`,
          padding: '6px 8px',
          color: designTokens.colors.gray[6],
        }}
      >
        {children}
      </td>
    ),
  }

  return (
    <div style={{ fontFamily: sans, fontSize: `${fontSize}px`, color: designTokens.colors.gray[6], overflowWrap: 'anywhere' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default RichContent
